import type { ClienteRef, DateOnlyString, UbicacionOrigen } from '../common';
import type { ResultadoVisita } from '../visitas/dto';

export interface DashboardDateBucket {
  fecha: string;
  total: string;
  /** Row count for the bucket; 0 means "no data yet", not a real zero. */
  conteo: number;
}

export interface DashboardPeriodBucket {
  fechaInicio: string;
  fechaFin: string;
  total: string;
  /** Row count for the bucket; 0 means "no data yet", not a real zero. */
  conteo: number;
}

export interface CobrosStats {
  currentDay: DashboardDateBucket;
  previousDay: DashboardDateBucket;
  currentMonth: DashboardPeriodBucket;
  previousMonth: DashboardPeriodBucket;
}

export interface PagosStats {
  currentDay: DashboardDateBucket;
  previousDay: DashboardDateBucket;
  currentMonth: DashboardPeriodBucket;
  previousMonth: DashboardPeriodBucket;
}

export interface ServicioCount {
  estadoKey: string;
  estadoValue: string;
  count: number;
}

/**
 * Monthly financial series: invoices by service-period date and cash receipts
 * by Guatemala registration date. The current month is month-to-date; closed
 * months are complete. Mora is derived from accumulated invoices, receipts,
 * and exceptions, seeded before the window and calibrated to current arrears.
 */
export interface SerieMensual {
  /** Month in YYYY-MM format. */
  mes: string;
  facturado: string;
  recaudado: string;
  mora: string;
}

/** Monthly client movement (movimiento de clientes) from the
 *  `cliente_evento` single source of truth. */
export interface MovimientoClienteMensual {
  /** Month in YYYY-MM format. */
  mes: string;
  /** Distinct clients with an ALTA event in that month. */
  altas: number;
  /** Distinct clients with a BAJA event in that month. */
  bajas: number;
  /** Distinct clients with a REACTIVACION event in that month. */
  reactivaciones: number;
}

export interface ClienteSaldo {
  id: number;
  nombre: string;
  ip: string | null;
  fechaCobro: string | null;
  /** Deprecated compatibility alias for `deudaPendiente`. */
  saldo: string;
  /** Two-value contract (preferred). Non-negative, never netted. */
  deudaPendiente: string;
  /** Two-value contract (preferred). Non-negative. */
  saldoAFavor: string;
}

/** Un día del año en la comparación plan vs real (heatmap anual). */
export interface PlanVsRealMonthDay {
  /** Day of month, 1..last day of that month (28/29 in February). */
  dia: number;
  /** Projected invoicing for the day: sum of plan.precio over active clients whose dia_cobro, clamped to the month's last day, equals `dia`. */
  plan: string;
  /** Real invoiced amount for the day (cobro_cliente by COALESCE(fecha_inicio, fecha_cobro), vigente filter). */
  real: string;
}

/** Un mes del año en la comparación plan vs real (heatmap anual). */
export interface PlanVsRealMonth {
  month: number; // 1..12
  days: PlanVsRealMonthDay[];
}

/** Comparación plan vs real del año seleccionado, mes a mes y día a día. */
export interface PlanVsRealYear {
  year: string; // 'YYYY'
  months: PlanVsRealMonth[]; // always 12 entries
}

/** One active financial application attached to the selected invoice. */
export interface FacturacionPorDiaAplicacion {
  id: number;
  idPago: number | null;
  idCredito: number | null;
  montoAplicado: string;
  /** Payment instant when the application came from a payment; null for credit consumption. */
  fechaPago: Date | null;
}

export interface FacturacionPorDiaFactura {
  id: number;
  fechaInicio: DateOnlyString;
  fechaFin: DateOnlyString;
  estadoKey: string | null;
  estadoValue: string | null;
  gross: string;
  montoAplicado: string;
  saldoPendiente: string;
  /** True whenever a matching invoice row was found, including historical rows. */
  generada: boolean;
  /** True only for a live invoice under the canonical billing predicate. */
  vigente: boolean;
  aplicaciones: FacturacionPorDiaAplicacion[];
}

export interface FacturacionPorDiaPeriodo {
  fechaInicio: DateOnlyString;
  fechaFin: DateOnlyString;
  /** A period is covered only when a live periodic invoice exists. */
  cubierta: boolean;
  /** Id of the deterministically selected live invoice, when covered. */
  facturaId: number | null;
  /** Number of live periodic invoices matching the expected period. */
  facturasVivas: number;
  /** Number of all matching periodic invoice rows, including annulled history. */
  facturasEncontradas: number;
}

/** Active client scheduled by the effective next billing cursor. */
export interface FacturacionPorDiaCliente {
  id: number;
  nombre: string;
  identificacion: string | null;
  ip: string | null;
  /** Authoritative effective next billing date from cliente.fecha_cobro. */
  fechaCobro: DateOnlyString;
  /** Configured billing day, retained to explain month-end clamping. */
  diaCobro: number | null;
  /** The selected next period; it is kept separate from the three prior periods. */
  siguientePeriodo: FacturacionPorDiaPeriodo;
  /** Includes historical/annulled rows, so generated is not equivalent to covered. */
  facturaSiguienteGenerada: boolean;
  /** Deterministically selected invoice for the future cursor slot, live first, historical otherwise. */
  facturaSiguiente: FacturacionPorDiaFactura | null;
  /** Latest periodic invoice before the authoritative cursor, including useful history. */
  ultimaFacturaGenerada: FacturacionPorDiaFactura | null;
  /** Exactly the three expected cursor slots immediately before siguientePeriodo, retained for detailed coverage evidence. */
  periodosRecientes: FacturacionPorDiaPeriodo[];
  /** Twelve expected cursor slots immediately before siguientePeriodo for compact dashboard coverage. */
  coberturaPeriodos: FacturacionPorDiaPeriodo[];
}

/** Active, billable clients whose effective next billing date has the selected day. */
export interface FacturacionPorDiaResponse {
  dia: number;
  data: FacturacionPorDiaCliente[];
  generatedAt: string;
}

export interface ClienteMapPoint {
  id: number;
  nombre: string;
  lat: number;
  lng: number;
  /** Datos opcionales del último pago; la ubicación del punto proviene del cliente. */
  ipCliente?: string | null;
  saldoInicial?: string | null;
  valor?: string | null;
  fechaInicio?: string | null;
  fechaFin?: string | null;
  estadoPago?: string | null;
  anulado?: boolean;
  /** Effective configured billing day (dia_cobro, falling back to fecha_cobro). */
  diaCobro?: number | null;
  tipoSuscripcionKey?: string | null;
  tipoSuscripcionValue?: string | null;
  deudaPendiente?: string | null;
  saldoAFavor?: string | null;
  facturasVencidas?: number;
  diasEnMora?: number;
}

/** Collector identity resolved for the operational payment route. */
export interface RutaCobroCobrador {
  id: string;
  nombre: string;
}

export const RUTA_COBROS_ESTADO = {
  VIGENTE: 'VIGENTE',
  ANULADO: 'ANULADO',
} as const;

export type RutaCobrosEstado = (typeof RUTA_COBROS_ESTADO)[keyof typeof RUTA_COBROS_ESTADO];

export const RUTA_COBROS_TIPO = {
  PAGO: 'PAGO',
  VISITA: 'VISITA',
} as const;

export type RutaCobrosTipo = (typeof RUTA_COBROS_TIPO)[keyof typeof RUTA_COBROS_TIPO];

interface RutaCobroEntryBase {
  id: number;
  idUsuario: string;
  idCliente: number;
  fechaCreacion: Date;
  latitud: number | null;
  longitud: number | null;
  ubicacionOrigen: UbicacionOrigen | null;
  cobrador: RutaCobroCobrador;
  cliente: ClienteRef | null;
}

/** One payment event registered in the selected business day. */
export interface RutaCobroPagoEntry extends RutaCobroEntryBase {
  tipo: typeof RUTA_COBROS_TIPO.PAGO;
  fechaPago: Date;
  valor: string;
  estadoPago: RutaCobrosEstado;
}

/** One visit registered in the selected business day, including its scheduled date. */
export interface RutaCobroVisitaEntry extends RutaCobroEntryBase {
  tipo: typeof RUTA_COBROS_TIPO.VISITA;
  /** Date selected for the visit (it may be a future rescheduled day). */
  fecha: DateOnlyString;
  resultado: ResultadoVisita;
  comentario: string | null;
}

export type RutaCobroEntry = RutaCobroPagoEntry | RutaCobroVisitaEntry;

/** Read-only operational activity projection for one America/Guatemala day. */
export interface RutaCobrosResponse {
  fecha: DateOnlyString;
  data: RutaCobroEntry[];
  /** All collectors with payments or visits registered on the selected day, independent of the active filter. */
  cobradores: RutaCobroCobrador[];
  generatedAt: string;
}

export interface DashboardResponse {
  cobros: CobrosStats;
  pagos: PagosStats;
  servicios: ServicioCount[];
  serieMensual: SerieMensual[];
  movimientoClientes: MovimientoClienteMensual[];
  clientesActivos: number;
  clientesTotal: number;
  /**
   * Cartera en mora: clientes con saldo negativo y monto adeudado (positivo).
   * `activos` excluye clientes de baja (deuda accionable); `conBaja` incluye
   * toda la cartera para contexto.
   */
  mora: {
    activos: { clientes: number; total: string };
    conBaja: { clientes: number; total: string };
  };
  saldosTop: ClienteSaldo[];
  /**
   * Free credit the company holds for its clients: the sum of AVAILABLE credit
   * lots (`cliente_credito.monto_disponible`) and how many clients hold them —
   * exactly what the app consumes on future invoices.
   *
   * Deliberately NOT `view_client_balance.saldo_a_favor`: since migration 0064
   * that column is the statement's per-client NET balance
   * (pagos + excepciones - cobros no anulados), so it also counts money already
   * reserved in prepaid packages (client 2937: 1,350.00 reserved, zero credit
   * lots). It is also NOT a reduction over `saldosTop`: that list feeds the
   * "Deudores" tab and only carries clients with open debt, so summing it can
   * only report the credit of people who owe money.
   */
  saldoAFavor: { total: string; clientes: number };
  mapPoints: ClienteMapPoint[];
  generatedAt: string;
}
